import {v4 as uuidv4} from "uuid";

interface Processing {
    expiration: number
    value: string
    result: Record<string, any>
}

// Processing object is used for preveting the same requests at the same time
const processing: Processing = {
    expiration: 100,
    value: uuidv4(), // it is needed to compare results returned by keys with this value for uniqueness
    result: {} // Hash object to keep key => result map. It is needed for returning pending promises by key from that object
};

export async function retryRequest<T = any>(
    requestCallback: () => Promise<T>,
    delays: number[] = [1000, 2000],
    shouldRetry?: (error: any) => boolean,
    key?: string
): Promise<T> {

    return new Promise(async (resolve, reject) => {

        // Check for we have a check for processing requests
        if (key?.length) {

            if (!processing.result[key]) {
                // No requests, so we have to set mock by the key for making next requests understand that something is being already processed
                processing.result[key] = processing.value;
            } else if (processing.value === processing.result[key]) {

                // Wait for result of cached request
                let waitingInterval = setInterval(() => {

                    if (processing.value !== processing.result[key]) {

                        resolve(processing.result[key]);
                        clearInterval(waitingInterval);
                    }

                }, 5);

                return;
            }
        }

        // Add first element as one ms
        delays.unshift(1);

        // New implementation
        for (let i = 0; i < delays.length; i++) {

            // Wait for next attempt
            await new Promise<void>((resolve) => setTimeout(() => resolve(), delays[i]))

            try {
                const result = await requestCallback()
                resolve(result)

                // Check for we need to keep result by key
                if (key) {

                    processing.result[key] = result;
                    setTimeout(() => delete processing.result[key], processing.expiration);
                }
                return;
            } catch (error) {
                if (shouldRetry && !shouldRetry(error) || i === delays.length - 1) {

                    reject(error);
                    return;
                }
            }
        }

        reject(new Error('unexpected'))
        return;
    });
}
// Client errors (4xx) will not change on retry, so only network and server errors are worth retrying
export function isRetryableError(error: any): boolean {

    const status = error?.response?.status;

    return !(status >= 400 && status < 500);
}
